'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, CheckCircle2, RefreshCw, AlertTriangle, FolderOpen, AlertCircle, BarChart3, TrendingUp } from 'lucide-react';
import AgentStats from './AgentStats';

interface ProjectStat {
    projectId: string;
    name: string;
    color: string;
    total: number;
    done: number;
    inProgress: number;
    issues: number;
    overdue: number;
    completionRate: number;
}

interface Alert {
    taskId: string;
    title: string;
    date: string;
    status: string;
    urgency: 'overdue' | 'today' | 'd-1' | 'd-3';
    projectName: string;
}

interface WeeklyTrend {
    week: string;
    done: number;
    added: number;
}

interface KPIData {
    summary: {
        totalProjects: number;
        totalTasks: number;
        done: number;
        inProgress: number;
        overdue: number;
        completionRate: number;
    };
    projectStats: ProjectStat[];
    alerts: Alert[];
    weeklyTrend: WeeklyTrend[];
}

export default function DashboardKPI({ isDark, onNavigateScheduler }: { isDark: boolean; onNavigateScheduler?: () => void }) {
    const [kpi, setKpi] = useState<KPIData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/dashboard/kpi')
            .then(r => r.json())
            .then(data => { setKpi(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!kpi || !kpi.summary) {
        return <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>KPI 데이터 없음</div>;
    }

    const urgencyConfig = {
        overdue: { label: '지연', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: <AlertCircle className="w-3.5 h-3.5 text-red-500" /> },
        today: { label: '오늘', bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: <AlertCircle className="w-3.5 h-3.5 text-orange-500" /> },
        'd-1': { label: 'D-1', bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', icon: <AlertCircle className="w-3.5 h-3.5 text-yellow-500" /> },
        'd-3': { label: 'D-3', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <AlertCircle className="w-3.5 h-3.5 text-blue-500" /> },
    };

    const maxTrend = Math.max(...kpi.weeklyTrend.map(w => Math.max(w.done, w.added)), 1);

    return (
        <div className="flex flex-col gap-3">
            {/* ── Row 1: 통합 KPI 카드 ── */}
            <div className="grid grid-cols-5 gap-2 max-lg:grid-cols-3 max-sm:grid-cols-2">
                {[
                    { label: '전체 일정', value: kpi.summary.totalTasks, unit: '건', color: 'from-blue-500 to-blue-600', icon: <ClipboardList className="w-5 h-5 text-blue-500" /> },
                    { label: '완료', value: kpi.summary.done, unit: `(${kpi.summary.completionRate}%)`, color: 'from-emerald-500 to-emerald-600', icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" /> },
                    { label: '진행중', value: kpi.summary.inProgress, unit: '건', color: 'from-purple-500 to-purple-600', icon: <RefreshCw className="w-5 h-5 text-purple-500" /> },
                    { label: '지연', value: kpi.summary.overdue, unit: '건', color: kpi.summary.overdue > 0 ? 'from-red-500 to-red-600' : 'from-gray-500 to-gray-600', icon: <AlertTriangle className={`w-5 h-5 ${kpi.summary.overdue > 0 ? 'text-red-500' : 'text-gray-500'}`} /> },
                    { label: '프로젝트', value: kpi.summary.totalProjects, unit: '개', color: 'from-amber-500 to-amber-600', icon: <FolderOpen className="w-5 h-5 text-amber-500" /> },
                ].map(k => (
                    <motion.div key={k.label} whileHover={{ y: -2 }}
                        className={`relative overflow-hidden rounded-xl p-3 border ${isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-lg">{k.icon}</span>
                            <div className={`text-xl font-bold bg-gradient-to-r ${k.color} bg-clip-text text-transparent`}>{k.value}</div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{k.label}</span>
                            <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{k.unit}</span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* ── Row 2: 프로젝트 진행률 + 주간 트렌드 ── */}
            <div className="grid grid-cols-3 gap-3 max-lg:grid-cols-1">
                {/* 프로젝트별 진행률 게이지 */}
                <div className={`col-span-2 rounded-xl border p-4 ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><BarChart3 className="w-4 h-4 text-indigo-500" /> 프로젝트별 진행률</h3>
                        {onNavigateScheduler && (
                            <button onClick={onNavigateScheduler} className={`text-[10px] px-2 py-1 rounded-md cursor-pointer ${isDark ? 'text-indigo-400 hover:bg-white/5' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                                스케줄러 →
                            </button>
                        )}
                    </div>
                    <div className="space-y-2.5 max-h-[200px] overflow-y-auto pr-1">
                        {kpi.projectStats.map(p => (
                            <div key={p.projectId}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`text-xs font-medium truncate max-w-[200px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{p.name}</span>
                                    <div className="flex items-center gap-2">
                                        {p.overdue > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">{p.overdue}지연</span>}
                                        <span className={`text-[10px] font-bold ${p.completionRate === 100 ? 'text-emerald-500' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{p.completionRate}%</span>
                                    </div>
                                </div>
                                <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${p.completionRate}%` }}
                                        transition={{ duration: 1, ease: 'easeOut' }}
                                        className="h-full rounded-full"
                                        style={{ background: `linear-gradient(90deg, ${p.color}, ${p.color}88)` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 주간 완료 추이 (미니 차트) */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><TrendingUp className="w-4 h-4 text-emerald-500" /> 주간 추이</h3>
                    <div className="flex items-end gap-1.5 h-[140px]">
                        {kpi.weeklyTrend.map((w, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <div className="w-full flex flex-col items-center justify-end flex-1 gap-0.5">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${(w.added / maxTrend) * 100}%` }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                        className={`w-full max-w-[20px] rounded-t-sm ${isDark ? 'bg-blue-500/30' : 'bg-blue-200'}`}
                                        title={`등록: ${w.added}건`}
                                    />
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${(w.done / maxTrend) * 100}%` }}
                                        transition={{ duration: 0.8, delay: i * 0.1 + 0.1 }}
                                        className={`w-full max-w-[20px] rounded-t-sm ${isDark ? 'bg-emerald-500/50' : 'bg-emerald-300'}`}
                                        title={`완료: ${w.done}건`}
                                    />
                                </div>
                                <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{w.week}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-4 mt-2">
                        <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-sm ${isDark ? 'bg-blue-500/30' : 'bg-blue-200'}`} />
                            <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>등록</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-sm ${isDark ? 'bg-emerald-500/50' : 'bg-emerald-300'}`} />
                            <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>완료</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Row 3: 지연/마감 경보 ── */}
            {kpi.alerts.length > 0 && (
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        마감 경보 <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600'}`}>{kpi.alerts.length}</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-1.5 max-h-[140px] overflow-y-auto pr-1 max-sm:grid-cols-1">
                        {kpi.alerts.map(a => {
                            const cfg = urgencyConfig[a.urgency];
                            return (
                                <div key={a.taskId} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${isDark ? `${cfg.bg} ${cfg.border}` : `${cfg.bg} ${cfg.border}`}`}>
                                    <span className="text-xs">{cfg.icon}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-xs font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{a.title}</div>
                                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                            {a.projectName} · {a.date} · <span className={cfg.text}>{cfg.label}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Row 4: 에이전트 통계 ── */}
            <AgentStats isDark={isDark} />
        </div>
    );
}
